// 手書きの小さな部品（shadcn は使わない）。色はすべて index.css のトークン経由。
import { forwardRef, useEffect, useRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { X } from 'lucide-react';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'ghost' | 'confirm' | 'danger';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-active',
  secondary: 'border border-line bg-surface text-ink-1 hover:bg-subtle',
  ghost: 'text-ink-2 hover:bg-subtle',
  confirm: 'bg-confirmed text-white hover:opacity-90',
  danger: 'bg-danger text-white hover:opacity-90',
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' }>(function Button(
  { variant = 'primary', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-ui font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-8 px-3 text-sm' : 'h-10 px-4 text-[15px]',
        VARIANT[variant],
        className,
      )}
      {...rest}
    />
  );
});

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('rounded-lg border border-line bg-surface p-4', className)} {...rest} />;
}

const field = 'w-full rounded-ui border border-line bg-surface px-3 text-ink-1 placeholder:text-ink-4 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-md';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(field, 'h-10', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(field, 'min-h-24 py-2 leading-relaxed', className)} {...rest} />;
});

type Tone = 'neutral' | 'draft' | 'confirmed' | 'alert' | 'danger' | 'accent';

const TONE: Record<Tone, string> = {
  neutral: 'bg-subtle text-ink-2',
  draft: 'bg-draft-lt text-draft',
  confirmed: 'bg-confirmed-lt text-confirmed',
  alert: 'bg-alert-lt text-alert',
  danger: 'bg-danger-lt text-danger',
  accent: 'bg-accent-lt text-accent',
};

export function Badge({ tone = 'neutral', className, ...rest }: HTMLAttributes<HTMLSpanElement> & { tone?: Tone }) {
  return <span className={cx('inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium leading-none whitespace-nowrap', TONE[tone], className)} {...rest} />;
}

/** <dialog> をそのまま使う。open が true の間だけ showModal する */
export function Dialog({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-lg border border-line bg-surface p-0 text-ink-1 shadow-xl"
    >
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <button type="button" onClick={onClose} aria-label="閉じる" className="rounded p-1 text-ink-3 hover:bg-subtle">
          <X size={18} />
        </button>
      </div>
      <div className="p-4">{open ? children : null}</div>
    </dialog>
  );
}

export function PageTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h1 className="text-xl font-semibold text-ink-1">{children}</h1>
      {actions}
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="rounded-ui bg-danger-lt px-3 py-2 text-sm text-danger">
      {children}
    </p>
  );
}

export function Muted({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx('text-sm text-ink-3', className)}>{children}</p>;
}

export function Loading() {
  return <Muted>読み込み中…</Muted>;
}

export { cx };
